import styled from "@emotion/styled";
import { FC, useEffect, useRef, useState } from "react";
import { Field, FieldRenderProps } from "react-final-form";
import { useSelector } from "react-redux";

import { LockOpen } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Heading,
  Icon,
  Modal,
  ModalHeader,
  ModalPortal,
  SpacingToken,
  Switch,
  Text,
} from "@livechat/design-system-react-components";

import { TextAreaAutosizeFieldFinalForm } from "components/design-system-adapters/TextAreaFieldFinalForm";
import { Divider } from "components/divider/Divider";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { IFormBlockOptions } from "store/entities/contact-forms/interfaces";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

interface IProps {
  name: string;
}

export const OptionsBlock: FC<IProps> = ({ name }) => {
  return <Field name={name} render={({ input, meta }) => <OptionsBlockInput input={input} meta={meta} />} />;
};

const OptionsBlockInput: FC<FieldRenderProps<IFormBlockOptions>> = ({ input }) => {
  const switchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (switchRef.current) {
      switchRef.current.disabled = false;
      // Hack to allow the switch to be clicked when state=locked
    }
  }, []);

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const isEnterprise = useSelector(selectCurrentSubscriptionPlan) === Plan.Enterprise;

  const handleToggleHideLabel = (): void => {
    if (isEnterprise) {
      input.onChange({
        ...input.value,
        poweredByFooter: !input.value.poweredByFooter,
      });
    } else {
      setIsUpsellModalOpen(true);
    }
  };

  return (
    <Flex column>
      <Field name={`${input.name}.confirmation`}>
        {({ input: confirmationInput, meta: confirmationMeta }) => (
          <FormField labelText="Confirmation after sending the form">
            <Textarea input={confirmationInput} meta={confirmationMeta} error={confirmationMeta.error} />
          </FormField>
        )}
      </Field>
      <Field name={`${input.name}.sendErrorMessage`}>
        {({ input: sendErrorMessageInput, meta: sendErrorMessageMeta }) => (
          <FormField labelText="Error message if sending failed">
            <Textarea input={sendErrorMessageInput} meta={sendErrorMessageMeta} error={sendErrorMessageMeta.error} />
          </FormField>
        )}
      </Field>
      <Divider />
      <Block marginBottom={withVar(SpacingToken.Spacing2)} />
      <Field name={`${input.name}.poweredByFooter`} type="checkbox">
        {({ input: poweredByFooterInput }) => (
          <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)}>
            <Switch
              on={!poweredByFooterInput.checked}
              onChange={handleToggleHideLabel}
              state={isEnterprise ? "regular" : "locked"}
              innerRef={switchRef}
            />

            <Text>Hide HelpDesk label</Text>
          </Flex>
        )}
      </Field>
      <ModalPortal zIndex={StackingContextLevel.Modal}>
        {isUpsellModalOpen && (
          <Modal
            css={{ maxWidth: "480px" }}
            onClose={() => setIsUpsellModalOpen(false)}
            heading={<ModalHeader />}
            footer={
              <Flex justifyContent="center" width="100%" paddingBottom={withVar(SpacingToken.Spacing10)}>
                <Button
                  href={`/subscription`}
                  kind="primary"
                  target="_blank"
                  onClick={() =>
                    trackEngagement(`Contact form: Whitelabel upgrade button pressed`, EventPlace.Settings)
                  }
                >
                  Upgrade to Enterprise
                </Button>
              </Flex>
            }
          >
            <Flex
              column
              alignItems="center"
              gap={withVar(SpacingToken.Spacing2)}
              paddingH={withVar(SpacingToken.Spacing10)}
              paddingBottom={withVar(SpacingToken.Spacing5)}
            >
              <Icon
                size="xxlarge"
                source={LockOpen}
                customColor={withVar(DesignToken.ContentBasicDisabled)}
                css={{ marginBottom: withVar(SpacingToken.Spacing1) }}
              />
              <Heading>Upgrade to hide HelpDesk label</Heading>
              <Text textAlign="center">
                Upgrade to Enterprise to hide the HelpDesk label on published contact forms, making them fully aligned
                to your company — no HelpDesk logos, just your brand.
              </Text>
            </Flex>
          </Modal>
        )}
      </ModalPortal>
    </Flex>
  );
};

const Textarea = styled(TextAreaAutosizeFieldFinalForm)`
  textarea {
    min-height: unset;
  }
`;
